import { createContext, use, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button, Icon, SearchField } from '@tutero/ui';

import { LIST_SEARCH, LIST_SEARCH_INPUT } from '@/lib/accounts/account-fields';
import { Loading } from '@/shell/Loading';
import { PageTitle } from '@/shell/PageTitle';

/**
 * Where a list under a page of tabs draws its search and its Add, in rows the page owns
 * (`learning-backend-is-one-page` D5, D6). The page's heading and tab already name the list, so
 * it draws no title. Empty until the page has mounted the two; null on no page of tabs.
 */
export const PageSlots = createContext<{
  add: HTMLElement | null;
  search: HTMLElement | null;
} | null>(null);

/** What the list is doing, for the one line under the toolbar that says so. */
interface ListStatus {
  /** Reading, so the page says it is. */
  loading: boolean;
  /** What the spinner is called, for a screen reader: *Loading regions*. */
  loadingLabel: string;
  /** The read failed: the sentence to show, the service's reason in it. */
  error?: string | null;
  /** The read found nothing: what to say instead of an empty table. */
  empty?: string | null;
}

/**
 * The page every list tool draws, so each sits in the same place on every page: the title, then
 * a toolbar right above the list with the search and the tool's own filters on the left and the
 * one way to add on the right, then the list.
 *
 * Only the arrangement is shared. What the list holds, how it is read and what a row opens stay
 * the tool's own (A3); this draws what it is handed.
 */
export function ListPage({
  title,
  add,
  search,
  filters,
  status,
  children,
  footer,
}: {
  title: string;
  add: { label: string; onAdd: () => void };
  search: {
    label: string;
    value: string;
    onChange: (value: string) => void;
  };
  /** The tool's own narrowings, drawn after the search. */
  filters?: ReactNode;
  status: ListStatus;
  /** The list itself, once there is one. */
  children?: ReactNode;
  /** Under the list: a pager, where the list is read a page at a time. */
  footer?: ReactNode;
}) {
  const slots = use(PageSlots);
  /* The search and Add at the user lists' size, so every list's toolbar reads alike. */
  const searchField = (
    <SearchField
      size="md"
      aria-label={search.label}
      placeholder={search.label}
      className={`${LIST_SEARCH} max-w-80 min-w-48 flex-1`}
      inputClassName={LIST_SEARCH_INPUT}
      value={search.value}
      onChange={(event) => search.onChange(event.target.value)}
    />
  );
  const addButton = (
    <Button size="sm" className="ml-auto" onClick={add.onAdd}>
      <Icon name="add" size={16} />
      {add.label}
    </Button>
  );
  return (
    /* `flex-1`, so the wait is centred in what is left under the toolbar (`Loading`). */
    <div className="flex w-full min-w-0 flex-1 flex-col gap-4">
      {slots ? (
        <>
          {slots.search && createPortal(searchField, slots.search)}
          {slots.add && createPortal(addButton, slots.add)}
          {filters ? (
            <div className="flex flex-wrap items-center gap-3">{filters}</div>
          ) : null}
        </>
      ) : (
        <>
          <PageTitle>{title}</PageTitle>
          {/* The toolbar sits on the list it narrows, with Add at its end. */}
          <div className="flex flex-wrap items-center gap-3">
            {searchField}
            {filters}
            {addButton}
          </div>
        </>
      )}

      {status.loading && !status.error ? (
        <Loading shape="table" label={status.loadingLabel} />
      ) : null}
      {status.error ? (
        <p role="alert" className="text-destructive text-regular-m">
          {status.error}
        </p>
      ) : null}
      {status.empty ? (
        <p className="text-muted-foreground text-regular-m">{status.empty}</p>
      ) : null}

      {children}
      {footer}
    </div>
  );
}

/** A list read a page at a time: where the reader is, and the two ways out of it. */
export function Pager({
  position,
  onBack,
  onNext,
}: {
  /** Where the reader is: *16–30 of 125*. */
  position: string;
  /** `null` on the first page. */
  onBack: (() => void) | null;
  /** `null` on the last page. */
  onNext: (() => void) | null;
}) {
  return (
    <nav aria-label="Pages" className="flex items-center gap-3">
      <span className="text-muted-foreground text-regular-s flex-1">
        {position}
      </span>
      <Button
        size="sm"
        variant="outline"
        disabled={onBack === null}
        onClick={onBack ?? undefined}
      >
        <Icon name="chevron_left" size={20} />
        Back
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={onNext === null}
        onClick={onNext ?? undefined}
      >
        Next
        <Icon name="chevron_right" size={20} />
      </Button>
    </nav>
  );
}
